import { useState } from 'react';
import clsx from 'clsx';
import { Badge, BadgeButton } from '../../catalyst/badge';
import { estimateEtaMinutes, formatEta, type Stage } from './helpers';
import { type StageDef } from './pipelineLayout';
import StageErrorsDialog from './StageErrorsDialog';

export default function StageNode({
  def,
  stage,
  avgRatePerMin,
}: {
  def: StageDef;
  stage: Stage | undefined;
  avgRatePerMin: number | undefined;
}) {
  const [errorsOpen, setErrorsOpen] = useState(false);
  const status = stage?.status ?? 'idle';
  const isIdle = status === 'idle' && !stage?.recentErrors;
  return (
    <div
      className={clsx(
        'rounded-lg border-2 px-3 py-2 text-center min-w-45',
        status === 'active' && 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/40',
        status === 'stalled' && 'border-red-500 bg-red-50 dark:bg-red-950/40',
        status === 'idle' && 'border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800',
        isIdle && 'opacity-50',
      )}
    >
      <div className="flex items-center justify-center gap-1 text-xs font-medium text-gray-900 dark:text-zinc-100">
        {def.label}
        {stage && stage.recentErrors > 0 && (
          <BadgeButton
            color="red"
            onClick={() => setErrorsOpen(true)}
            title="Show recent errors"
            aria-label="Show recent errors"
            className="cursor-pointer"
          >
            {stage.recentErrors} err
          </BadgeButton>
        )}
        {stage?.unavailable && <Badge color="zinc">stats unavailable</Badge>}
      </div>
      <div
        className={clsx(
          'text-sm font-semibold',
          status === 'active' && 'text-emerald-600 dark:text-emerald-400',
          status === 'stalled' && 'text-red-600 dark:text-red-400',
          status === 'idle' && 'text-gray-500 dark:text-zinc-400',
        )}
      >
        {status === 'stalled'
          ? `${stage?.ratePerMin ?? 0}/min ⚠ stalled`
          : status === 'active'
            ? `${stage?.ratePerMin}/min`
            : 'idle'}
      </div>
      <div className="text-xs text-gray-500 dark:text-zinc-400">
        {stage ? `${stage.instances} inst · ${stage.inFlight} in-flight` : '—'}
      </div>
      {stage && stage.queueDepth > 0 && (
        <div className="text-xs text-gray-500 dark:text-zinc-400">
          {formatEta(estimateEtaMinutes(stage.queueDepth, avgRatePerMin ?? 0))} to clear
        </div>
      )}
      {/* Mounted unconditionally: the 5s pipeline-status poll can drop recentErrors
          to 0 while the dialog is open, which would unmount it mid-read and leave
          stale open-state behind for a ghost reopen. */}
      <StageErrorsDialog stageId={def.id} stageLabel={def.label} open={errorsOpen} onClose={() => setErrorsOpen(false)} />
    </div>
  );
}
